// Block tree mutations such as add, remove, reorder and settings updates.
import DashboardBlockHelper from "../helpers/DashboardBlockHelper";

const dashboardLayoutEditorBlocks = {
    /**
     * Check whether a block is a checks accordion container widget.
     *
     * @param {*} block
     */
    isChecksAccordionBlock(block) {
        return block?.type === 'cms-dashboard-checks-accordion';
    },
    /**
     * Return the checks accordion block for the currently active tab.
     */
    getChecksAccordionBlockForActiveTab() {
        const activeTabId = this.getActiveDashboardTabId();

        return this.blocks.find((block) => this.isChecksAccordionBlock(block) && (block.tabId || this.getDefaultDashboardTabId()) === activeTabId) || null;
    },
    /**
     * Get normalized child reference ids for one checks accordion block.
     *
     * @param {*} accordionBlock
     */
    getChecksAccordionChildReferenceIds(accordionBlock) {
        if (!accordionBlock) {
            return [];
        }

        const childReferenceIds = accordionBlock?.settings?.values?.childWidgetReferenceIds;

        if (!Array.isArray(childReferenceIds)) {
            return [];
        }

        return childReferenceIds
            .map((referenceId) => String(referenceId || '').trim())
            .filter(Boolean);
    },
    /**
     * Persist child reference ids on one checks accordion block.
     *
     * @param {*} accordionBlock
     * @param {*} childReferenceIds
     */
    setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds) {
        if (!accordionBlock) {
            return;
        }

        const existingSettings = accordionBlock?.settings && typeof accordionBlock.settings === 'object'
            ? accordionBlock.settings
            : {
                values: {},
                hasForm: false,
            };
        const existingValues = existingSettings?.values && typeof existingSettings.values === 'object'
            ? existingSettings.values
            : {};

        this.$set(accordionBlock, 'settings', {
            ...existingSettings,
            values: {
                ...existingValues,
                childWidgetReferenceIds: [...childReferenceIds],
            },
        });
    },
    /**
     * Return child blocks for the active checks accordion in configured order.
     */
    getChecksAccordionChildBlocksForActiveTab() {
        const accordionBlock = this.getChecksAccordionBlockForActiveTab();

        if (!accordionBlock) {
            return [];
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock);
        const childBlocks = [];

        for (const childReferenceId of childReferenceIds) {
            const block = DashboardBlockHelper.getBlock(this.blocks, childReferenceId);

            if (!block || this.isChecksAccordionBlock(block)) {
                continue;
            }

            childBlocks.push(block);
        }

        return childBlocks;
    },
    /**
     * Check whether one block reference id is currently configured as child of
     * any checks accordion in the active tab.
     *
     * @param {*} referenceId
     */
    isChecksAccordionChildInActiveTab(referenceId) {
        return this.getChecksAccordionChildBlocksForActiveTab().some((block) => block.referenceId === referenceId);
    },
    /**
     * Add one child reference to the active checks accordion list.
     *
     * @param {*} childReferenceId
     */
    addChildToActiveChecksAccordion(childReferenceId) {
        const accordionBlock = this.getChecksAccordionBlockForActiveTab();

        if (!accordionBlock || !childReferenceId) {
            return;
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock);

        if (childReferenceIds.includes(childReferenceId)) {
            return;
        }

        childReferenceIds.push(childReferenceId);
        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Add a widget to the active checks accordion and commit current drag.
     *
     * @param {*} childReferenceId
     */
    addChecksAccordionChild(childReferenceId) {
        const block = DashboardBlockHelper.getBlock(this.blocks, childReferenceId);

        if (!block || this.isChecksAccordionBlock(block)) {
            return;
        }

        block.tabId = this.getActiveDashboardTabId();
        this.addChildToActiveChecksAccordion(childReferenceId);
        this.breakpoints.forEach((breakpoint) => {
            const positioning = this.getBreakpointPositioningEntry(childReferenceId, breakpoint.id);

            if (positioning) {
                positioning.hidden = true;
            }
        });
        this.dashboardCanvasCommittedReferenceId = childReferenceId;
        this.dashboardCanvasDragReferenceId = null;
        this.activeBlock = null;
    },
    /**
     * Get child blocks for one checks accordion block.
     *
     * @param {*} accordionReferenceId
     */
    getChecksAccordionChildBlocks(accordionReferenceId) {
        const accordionBlock = DashboardBlockHelper.getBlock(this.blocks, accordionReferenceId);

        if (!accordionBlock || !this.isChecksAccordionBlock(accordionBlock)) {
            return [];
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock);
        const childBlocks = [];

        for (const childReferenceId of childReferenceIds) {
            const block = DashboardBlockHelper.getBlock(this.blocks, childReferenceId);

            if (!block || this.isChecksAccordionBlock(block)) {
                continue;
            }

            childBlocks.push(block);
        }

        return childBlocks;
    },
    /**
     * Add one child to a specific checks accordion block.
     *
     * @param {*} accordionReferenceId
     * @param {*} childReferenceId
     */
    addChildToChecksAccordion(accordionReferenceId, childReferenceId) {
        const accordionBlock = DashboardBlockHelper.getBlock(this.blocks, accordionReferenceId);

        if (!accordionBlock || !this.isChecksAccordionBlock(accordionBlock) || !childReferenceId) {
            return;
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock);

        if (childReferenceIds.includes(childReferenceId)) {
            return;
        }

        childReferenceIds.push(childReferenceId);
        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Remove one child from a specific checks accordion block.
     *
     * @param {*} accordionReferenceId
     * @param {*} childReferenceId
     */
    removeChildFromChecksAccordion(accordionReferenceId, childReferenceId) {
        const accordionBlock = DashboardBlockHelper.getBlock(this.blocks, accordionReferenceId);

        if (!accordionBlock || !this.isChecksAccordionBlock(accordionBlock) || !childReferenceId) {
            return;
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock)
            .filter((referenceId) => referenceId !== childReferenceId);

        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Move one child inside a specific checks accordion block.
     *
     * @param {*} accordionReferenceId
     * @param {*} childReferenceId
     * @param {*} targetIndex
     */
    moveChildInChecksAccordion(accordionReferenceId, childReferenceId, targetIndex) {
        const accordionBlock = DashboardBlockHelper.getBlock(this.blocks, accordionReferenceId);

        if (!accordionBlock || !this.isChecksAccordionBlock(accordionBlock) || !childReferenceId) {
            return;
        }

        const childReferenceIds = [...this.getChecksAccordionChildReferenceIds(accordionBlock)];
        const currentIndex = childReferenceIds.findIndex((referenceId) => referenceId === childReferenceId);

        if (currentIndex < 0) {
            return;
        }

        childReferenceIds.splice(currentIndex, 1);
        const safeTargetIndex = Math.max(0, Math.min(Number(targetIndex || 0), childReferenceIds.length));
        childReferenceIds.splice(safeTargetIndex, 0, childReferenceId);
        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Drop a dragged widget into a checks accordion block.
     *
     * @param {*} accordionReferenceId
     * @param {*} targetIndex
     */
    handleChecksAccordionDrop(accordionReferenceId, targetIndex = null) {
        const childReferenceId = this.dashboardCanvasDragReferenceId;
        const block = DashboardBlockHelper.getBlock(this.blocks, childReferenceId);

        if (!childReferenceId || !block || this.isChecksAccordionBlock(block)) {
            return;
        }

        block.tabId = this.getActiveDashboardTabId();
        this.addChildToChecksAccordion(accordionReferenceId, childReferenceId);

        if (targetIndex !== null) {
            this.moveChildInChecksAccordion(accordionReferenceId, childReferenceId, targetIndex);
        }

        this.breakpoints.forEach((breakpoint) => {
            const positioning = this.getBreakpointPositioningEntry(childReferenceId, breakpoint.id);

            if (positioning) {
                positioning.hidden = true;
            }
        });

        this.dashboardCanvasCommittedReferenceId = childReferenceId;
        this.dashboardCanvasDragReferenceId = null;
        this.activeBlock = null;
    },
    /**
     * Remove one child reference from the active checks accordion list.
     *
     * @param {*} childReferenceId
     */
    removeChildFromActiveChecksAccordion(childReferenceId) {
        const accordionBlock = this.getChecksAccordionBlockForActiveTab();

        if (!accordionBlock || !childReferenceId) {
            return;
        }

        const childReferenceIds = this.getChecksAccordionChildReferenceIds(accordionBlock)
            .filter((referenceId) => referenceId !== childReferenceId);

        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Move one child reference to a new index in the active checks accordion list.
     *
     * @param {*} childReferenceId
     * @param {*} targetIndex
     */
    moveChildInActiveChecksAccordion(childReferenceId, targetIndex) {
        const accordionBlock = this.getChecksAccordionBlockForActiveTab();

        if (!accordionBlock || !childReferenceId) {
            return;
        }

        const childReferenceIds = [...this.getChecksAccordionChildReferenceIds(accordionBlock)];
        const currentIndex = childReferenceIds.findIndex((referenceId) => referenceId === childReferenceId);

        if (currentIndex < 0) {
            return;
        }

        childReferenceIds.splice(currentIndex, 1);
        const safeTargetIndex = Math.max(0, Math.min(Number(targetIndex || 0), childReferenceIds.length));
        childReferenceIds.splice(safeTargetIndex, 0, childReferenceId);
        this.setChecksAccordionChildReferenceIds(accordionBlock, childReferenceIds);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Get breakpoint positioning entry.
     *
     * @param {*} referenceId
     * @param {*} breakpointId
     */
    getBreakpointPositioningEntry(referenceId, breakpointId = this.activeBreakpointId) {
        const breakpoint = this.breakpoints.find((currentBreakpoint) => currentBreakpoint.id === breakpointId);

        if (!breakpoint) {
            return null;
        }

        return breakpoint.widgetPositioning.find((positioning) => positioning.referenceId === referenceId) || null;
    },
    /**
     * Visibility is stored on the breakpoint-specific positioning record so one
     * widget can stay visible on desktop and be hidden on tablet or phone.
     */
    setDashboardBlockHidden(referenceId, hidden, breakpointId = this.activeBreakpointId) {
        const positioning = this.getBreakpointPositioningEntry(referenceId, breakpointId);

        if (!positioning) {
            return;
        }

        positioning.hidden = Boolean(hidden);
        this.dashboardCanvasContextMenuReferenceId = null;
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Hide dashboard block for active breakpoint.
     *
     * @param {*} referenceId
     */
    hideDashboardBlockForActiveBreakpoint(referenceId) {
        this.setDashboardBlockHidden(referenceId, true);
    },
    /**
     * Show dashboard block for active breakpoint.
     *
     * @param {*} referenceId
     */
    showDashboardBlockForActiveBreakpoint(referenceId) {
        this.setDashboardBlockHidden(referenceId, false);
    },
    // A newly added block exists only in the editor until it gets committed or discarded.
    /**
     * Clear committed active block.
     */
    clearCommittedActiveBlock() {
        this.dashboardCanvasCommittedReferenceId = null;
        this.activeBlock = null;
    },
    /**
     * Remove active block draft.
     */
    removeActiveBlockDraft() {
        this.deleteWidget(this.blocks, this.activeBlock.referenceId, true, false);
    },
    /**
     * Active widget type changed.
     *
     * @param {*} widget
     */
    activeWidgetTypeChanged(widget) {
        if (!widget) {
            if (this.activeBlock) {
                if (this.dashboardCanvasCommittedReferenceId === this.activeBlock.referenceId) {
                    this.clearCommittedActiveBlock();
                    return;
                }

                this.removeActiveBlockDraft();
            }
        }

        this.activeBlock = widget;
    },
    /**
     * Active block type changed.
     *
     * @param {*} widget
     */
    activeBlockTypeChanged(widget) {
        this.activeWidgetTypeChanged(widget);
    },
    /**
     * Give a newly created widget a free initial canvas position. This keeps
     * the temporary widget preview from rendering on top of an existing block
     * before its drag-and-drop position is committed.
     *
     * @param {*} newBlock
     */
    positionNewWidgetAtFirstAvailableCanvasPosition(newBlock) {
        const startColumn = Math.max(Number(newBlock.defaultColumn ?? newBlock.column ?? 1), 1);
        const startRow = Math.max(Number(newBlock.defaultRow ?? newBlock.row ?? 1), 1);
        const requestedColumnSpan = Math.min(
            Math.max(Number(newBlock.columnSpan ?? newBlock.colspan ?? 1), 1),
            this.dashboardGridColumns,
        );
        const minimumColumnSpan = Math.min(
            Math.max(Number(newBlock.minColspan ?? 1), 1),
            requestedColumnSpan,
        );
        const requestedRowSpan = Math.min(
            Math.max(Number(newBlock.rowSpan ?? 1), 1),
            this.dashboardGridRows,
        );
        let target = null;

        // Try the default size first, then use the widest available placement
        // and, within that width, the tallest available height. Width never
        // falls below minColspan.
        for (let columnSpan = requestedColumnSpan; columnSpan >= minimumColumnSpan && !target; columnSpan -= 1) {
            for (let rowSpan = requestedRowSpan; rowSpan >= 1 && !target; rowSpan -= 1) {
                target = this.findDashboardDropTarget(
                    startColumn,
                    startRow,
                    {
                        ...newBlock,
                        columnSpan,
                        colspan: columnSpan,
                        rowSpan,
                    },
                    newBlock.referenceId,
                );
            }
        }

        if (!target) {
            return;
        }

        newBlock.columnSpan = target.columnSpan;
        newBlock.colspan = target.columnSpan;
        newBlock.rowSpan = target.rowSpan;

        this.breakpoints.forEach((breakpoint) => {
            const positioning = breakpoint.widgetPositioning.find((item) => item.referenceId === newBlock.referenceId);

            if (!positioning) {
                return;
            }

            positioning.offset = target.column - 1;
            positioning.row = target.row;
            positioning.colspan = target.columnSpan;
            positioning.rowSpan = target.rowSpan;
        });
    },
    /**
     * Add new widget.
     *
     * @param {*} newBlock
     */
    addNewWidget(newBlock) {
        this.$set(newBlock, 'tabId', this.getActiveDashboardTabId());
        this.blocks.push(newBlock);

        this.breakpoints.forEach((breakpoint) => {
            breakpoint.widgetPositioning.push(this.dataProvider.getWidgetPositioningForNewWidget(newBlock.referenceId, newBlock));
        });

        this.positionNewWidgetAtFirstAvailableCanvasPosition(newBlock);
        this.dashboardCanvasDragReferenceId = newBlock.referenceId;
    },
    /**
     * Add new block.
     *
     * @param {*} newBlock
     */
    addNewBlock(newBlock) {
        this.addNewWidget(newBlock);
    },
    // Keep the recursive delete in one helper so the public deleteWidget method
    // stays small and only describes the side effects.
    /**
     * Delete widget from list.
     *
     * @param {*} blocks
     * @param {*} referenceId
     * @param {*} updateOrderIndexes
     */
    deleteWidgetFromList(blocks, referenceId, updateOrderIndexes) {
        for (let index = 0; index < blocks.length; index += 1) {
            const block = blocks[index];

            if (block.referenceId === referenceId) {
                blocks.splice(index, 1);

                if (updateOrderIndexes) {
                    this.updateOrderIndexes(blocks);
                }

                return true;
            }

            const childBlocks = Array.isArray(block.blocks) ? block.blocks : [];
            const deletedChild = this.deleteWidgetFromList(childBlocks, referenceId, updateOrderIndexes);

            if (deletedChild) {
                return true;
            }
        }

        return false;
    },
    /**
     * Delete widget.
     *
     * @param {*} blocks
     * @param {*} referenceId
     * @param {*} removePositioning
     * @param {*} updateOrderIndexes
     */
    deleteWidget(blocks, referenceId, removePositioning = false, updateOrderIndexes = true) {
        this.deleteWidgetFromList(blocks, referenceId, updateOrderIndexes);
        this.blocks.forEach((block) => {
            if (!this.isChecksAccordionBlock(block)) {
                return;
            }

            const filteredChildIds = this.getChecksAccordionChildReferenceIds(block)
                .filter((childReferenceId) => childReferenceId !== referenceId);

            this.setChecksAccordionChildReferenceIds(block, filteredChildIds);
        });

        if (removePositioning) {
            this.deleteWidgetPositioning(referenceId);
        }
    },
    /**
     * Canvas block actions still speak in "dashboard block" terms, but the
     * stored editor model is a flat widget list. Keep this alias so the
     * template stays readable and the delete flow removes both the block and
     * its positioning records.
     */
    deleteDashboardBlock(referenceId) {
        this.deleteWidget(this.blocks, referenceId, true, true);
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Delete widget positioning.
     *
     * @param {*} referenceId
     */
    deleteWidgetPositioning(referenceId) {
        this.breakpoints.forEach((breakpoint) => {
            const indexToDelete = breakpoint.widgetPositioning.findIndex((blockPosition) => blockPosition.referenceId === referenceId);
            breakpoint.widgetPositioning.splice(indexToDelete, 1);
        });
    },
    /**
     * Update order indexes.
     *
     * @param {*} blocks
     */
    updateOrderIndexes(blocks) {
        this.breakpoints.forEach((breakpoint) => {
            this.getWidgetsSortedByPosition(blocks, breakpoint.id).forEach((sortedBlock, orderIndex) => {
                this.setOrderIndexForBlock(orderIndex, sortedBlock, breakpoint);
            });
        });
    },
    /**
     * Get widgets sorted by position.
     *
     * @param {*} blocks
     * @param {*} breakpointId
     */
    getWidgetsSortedByPosition(blocks, breakpointId) {
        const sortedBlocks = [...blocks];

        sortedBlocks.sort((oldBlock, newBlock) => {
            const oldBlockPosition = this.getBlockPositioning(oldBlock.referenceId, breakpointId);
            const newBlockPosition = this.getBlockPositioning(newBlock.referenceId, breakpointId);

            if (oldBlockPosition.orderIndex < newBlockPosition.orderIndex) {
                return -1;
            }

            if (oldBlockPosition.orderIndex > newBlockPosition.orderIndex) {
                return 1;
            }

            return 0;
        });

        return sortedBlocks;
    },
    /*
     * Original breakpoint order is mirrored to the derived breakpoints so all
     * canvas variants keep the same widget ordering unless explicitly changed.
     */
    setOrderIndexAcrossBreakpoints(referenceId, orderIndex) {
        this.breakpoints.forEach((breakpointItem) => {
            const breakpointPositioning = breakpointItem.widgetPositioning.find((widgetPositioning) => widgetPositioning.referenceId === referenceId);

            if (!breakpointPositioning) {
                return;
            }

            breakpointPositioning.orderIndex = orderIndex;
        });
    },
    /**
     * Set order index for block.
     *
     * @param {*} orderIndex
     * @param {*} block
     * @param {*} breakpoint
     */
    setOrderIndexForBlock(orderIndex, block, breakpoint) {
        const positioning = breakpoint.widgetPositioning.find((widgetPositioning) => widgetPositioning.referenceId === block.referenceId);

        if (breakpoint.isOriginal || positioning.orderIndex === null) {
            this.setOrderIndexAcrossBreakpoints(block.referenceId, orderIndex);
        }

        positioning.orderIndex = orderIndex;

        this.triggerDataProviderEvent('orderChanged');
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Widget property changed.
     *
     * @param {*} properties
     */
    widgetPropertyChanged(properties) {
        const referenceId = properties.referenceId;
        const block = DashboardBlockHelper.getBlock(this.blocks, referenceId);

        delete properties.referenceId;

        Object.keys(properties).forEach((key) => {
            block[key] = properties[key];
        });

        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Block property changed.
     *
     * @param {*} properties
     */
    blockPropertyChanged(properties) {
        this.widgetPropertyChanged(properties);
    },
    /**
     * Get block positioning.
     *
     * @param {*} referenceId
     * @param {*} breakpointId
     */
    getBlockPositioning(referenceId, breakpointId) {
        const breakpoint = this.breakpoints.find((currentBreakpoint) => currentBreakpoint.id === breakpointId);
        return breakpoint?.widgetPositioning.find((widgetPositioning) => widgetPositioning?.referenceId === referenceId);
    },
    /**
     * Save settings.
     *
     * @param {*} $event
     */
    saveSettings($event) {
        const block = DashboardBlockHelper.getBlock(this.blocks, $event.referenceId);
        const existingSettings = block?.settings && typeof block.settings === 'object'
            ? block.settings
            : {
                values: {},
                hasForm: false,
            };
        const nextValues = $event.settings?.values && typeof $event.settings.values === 'object'
            ? $event.settings.values
            : $event.settings;
        const mergedValues = {
            ...(existingSettings.values || {}),
            ...(nextValues || {}),
        };

        if (block?.type === 'cms-dashboard-page-status-check') {
            [
                'statusLabel',
                'statusValue',
                'statusDescription',
                'isEnabled',
                'issueCount',
                'criticalIssueCount',
                'href',
                'target',
            ].forEach((runtimeKey) => {
                delete mergedValues[runtimeKey];
            });
        }

        this.$set(block, 'settings', {
            ...existingSettings,
            values: mergedValues,
        });
    },
};

export default dashboardLayoutEditorBlocks;
